import { defineStore } from "pinia";
import { toRaw } from "vue";
import { compareIdentifiers } from "@persolog/utils";
import { LanguageManagementAPI } from "../api";
import type { ProductActivation, Identifier } from "../types/PersologOnlineTypes";
import type { MaintenanceLanguage, MaintenanceData, MaintenanceItem } from "../types/TextMaintenanceTypes";
import {
    saveAllChangesToBackend,
    extractLanguagesWithChanges,
} from "../utils/textMaintenanceUtils";
import {
    importCSVDataImpl,
    generateCSVContentFromRawData,
    type ImportType,
    type ImportResult,
} from "../composables/useCSVOperations";

// ─── Domain model ───────────────────────────────────────────────────────────
//
// The feature has three domains: profile, questionnaire and default ("texts").
// They used to be three hand-duplicated code paths with two parallel change
// systems (per-item flags + per-tab string-sentinel maps). Here every domain is
// modeled identically and there is ONE typed change model (`ItemChange`).

export const DOMAIN_KEYS = ["profile", "questionnaire", "default"] as const;
export type DomainKey = (typeof DOMAIN_KEYS)[number];

/** The single source of truth for a pending edit on one item (keyed by item id). */
export interface ItemChange {
    identifier: string; // replaces the old __IDENTIFIER__ sentinel
    texts: Record<string, string>; // changed/new text per language
    newLanguages?: Record<string, boolean>; // replaces __NEW_<lang>__ sentinels
    isNew?: boolean;
    isDeleted?: boolean; // replaces __DELETED__ sentinel
}

interface DomainState {
    raw: Identifier[]; // full fetched dataset (all languages) — never partial
    items: MaintenanceItem[]; // rendered rows (projection of raw + changes)
    changes: Record<string, ItemChange>; // pending edits, keyed by item id
    originals: Record<string, Record<string, string>>; // original text per item/lang (reset baseline)
}

function emptyDomain(): DomainState {
    return { raw: [], items: [], changes: {}, originals: {} };
}

// ─── Tab / importType ↔ domain mapping ────────────────────────────────────────

function domainKeyForTab(tabIndex: number): DomainKey {
    if (tabIndex === 1) return "questionnaire";
    if (tabIndex === 2) return "default";
    return "profile";
}

function tabIndexForImportType(importType: string): number {
    if (importType === "questionnaire") return 1;
    if (importType === "texts") return 2;
    return 0;
}

function domainKeyForImportType(importType: string): DomainKey {
    if (importType === "questionnaire") return "questionnaire";
    if (importType === "texts") return "default";
    return "profile";
}

// ─── Product fetch helpers (ported from the old data processor) ───────────────

function extractBaseProductId(name: string): string {
    return name.startsWith("profile-") || name.startsWith("questionnaire-")
        ? name.split("-").slice(1).join("-")
        : name;
}

async function fetchProductData(productType: string, productId: string, language: string) {
    try {
        const data = await LanguageManagementAPI.getTextsForProductAndLanguage("STAGING", productType, language);
        return data.domainTexts || [];
    } catch (error) {
        console.warn(`Failed to fetch ${productType} data for ${productId} in language ${language}:`, error);
        return [];
    }
}

function addTextsToMap(textsMap: Map<string, any[]>, domainTexts: any[], language: string) {
    for (const domainText of domainTexts) {
        if (!textsMap.has(domainText.identifier)) {
            textsMap.set(domainText.identifier, []);
        }
        textsMap.get(domainText.identifier)!.push({
            id: textsMap.get(domainText.identifier)!.length + 1,
            text: domainText.text,
            language,
        });
    }
}

function getAvailableLanguageCodes(productId: string, availableProducts: ProductActivation[]): Set<string> {
    const codes = new Set<string>();
    for (const product of availableProducts) {
        if (product.name.includes(productId)) {
            for (const language of product.languages) codes.add(language);
        }
    }
    if (codes.size === 0) {
        console.warn(`No languages found for product ${productId}, using fallback languages`);
        codes.add("en");
        codes.add("de");
    }
    return codes;
}

async function fetchLanguageData(
    productId: string,
    language: string,
    profileTextsMap: Map<string, any[]>,
    questionnaireTextsMap: Map<string, any[]>,
    defaultTextsMap: Map<string, any[]>
) {
    const profileTexts = await fetchProductData(`profile-${productId}`, productId, language);
    const hasProfile = profileTexts.length > 0;
    if (hasProfile) addTextsToMap(profileTextsMap, profileTexts, language);

    const questionnaireTexts = await fetchProductData(`questionnaire-${productId}`, productId, language);
    const hasQuestionnaire = questionnaireTexts.length > 0;
    if (hasQuestionnaire) addTextsToMap(questionnaireTextsMap, questionnaireTexts, language);

    if (!hasProfile && !hasQuestionnaire) {
        const defaultTexts = await fetchProductData(productId, productId, language);
        if (defaultTexts.length > 0) addTextsToMap(defaultTextsMap, defaultTexts, language);
    }
}

/**
 * Loads every namespace × language for a product in a single request and partitions
 * the rows into the profile/questionnaire/default maps.
 * Returns whether the maps were populated, and whether the endpoint is missing (404/405)
 * so the caller can stop probing it for the rest of the session.
 */
async function populateMapsViaBulk(
    productId: string,
    profileTextsMap: Map<string, any[]>,
    questionnaireTextsMap: Map<string, any[]>,
    defaultTextsMap: Map<string, any[]>
): Promise<{ populated: boolean; endpointMissing: boolean }> {
    try {
        const rows = await LanguageManagementAPI.getAllTextsForProduct("STAGING", productId);
        for (const row of rows) {
            let target: Map<string, any[]>;
            if (row.product.startsWith("profile-")) {
                target = profileTextsMap;
            } else if (row.product.startsWith("questionnaire-")) {
                target = questionnaireTextsMap;
            } else {
                target = defaultTextsMap;
            }
            addTextsToMap(target, row.domainTexts, row.language);
        }
        return { populated: true, endpointMissing: false };
    } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        const endpointMissing = status === 404 || status === 405;
        console.warn("Bulk product fetch unavailable; falling back to per-language fetch.", error);
        return { populated: false, endpointMissing };
    }
}

function convertMapToIdentifiers(textsMap: Map<string, any[]>): Identifier[] {
    const result: Identifier[] = [];
    let idCounter = 1;
    for (const [identifier, texts] of textsMap.entries()) {
        result.push({ id: idCounter++, identifier, texts });
    }
    return result;
}

// ─── Change-model helpers (pure) ──────────────────────────────────────────────

interface MergedChange {
    texts: Record<string, string>;
    newLanguages: Record<string, boolean>;
    isNew: boolean;
    isDeleted: boolean;
}

const EMPTY_MERGED: MergedChange = { texts: {}, newLanguages: {}, isNew: false, isDeleted: false };

/**
 * Groups every pending change by its identifier in a single O(changes) pass.
 * This is needed because a change can be recorded under an item id that differs
 * from the id the next rebuild produces (e.g. CSV-imported / freshly-added items).
 * Doing it once (rather than scanning all changes per identifier) keeps a table
 * rebuild O(identifiers + changes) instead of O(identifiers × changes).
 */
function groupChangesByIdentifier(changes: Record<string, ItemChange>): Map<string, MergedChange> {
    const map = new Map<string, MergedChange>();
    for (const change of Object.values(changes)) {
        let merged = map.get(change.identifier);
        if (!merged) {
            merged = { texts: {}, newLanguages: {}, isNew: false, isDeleted: false };
            map.set(change.identifier, merged);
        }
        Object.assign(merged.texts, change.texts);
        Object.assign(merged.newLanguages, change.newLanguages ?? {});
        if (change.isNew) merged.isNew = true;
        if (change.isDeleted) merged.isDeleted = true;
    }
    return map;
}

function buildLanguagesToInclude(
    allChangedLanguages: string[],
    identifierTexts: Array<{ language: string }>,
    selectedLanguages: string[]
): Set<string> {
    // When an item has tracked changes we keep ALL of its languages (raw + selected
    // + changed) so imported-but-not-displayed languages survive table rebuilds.
    if (allChangedLanguages.length > 0) {
        return new Set([...identifierTexts.map((t) => t.language), ...selectedLanguages, ...allChangedLanguages]);
    }
    return new Set(selectedLanguages);
}

function shouldKeepImportedItem(
    selectedLanguages: string[],
    texts: Record<string, string>,
    changedLanguages: string[],
    isDeleted: boolean
): boolean {
    const hasTextInSelected = selectedLanguages.some((lang) => texts[lang] && texts[lang].trim() !== "");
    return hasTextInSelected || changedLanguages.length > 0 || isDeleted;
}

function assembleMaintenanceItem(
    itemId: string,
    identifierName: string,
    texts: Record<string, string>,
    change: ItemChange | undefined,
    merged: MergedChange
): MaintenanceItem {
    const item: MaintenanceItem = {
        id: itemId,
        identifier: identifierName,
        texts,
        originalTexts: { ...texts },
    };

    const newTexts: Record<string, boolean> = {};
    for (const lang of Object.keys(texts)) {
        if (change?.newLanguages?.[lang] || merged.newLanguages[lang]) newTexts[lang] = true;
    }
    if (Object.keys(newTexts).length > 0) item.newTexts = newTexts;

    if (change?.isDeleted || merged.isDeleted) item.isDeleted = true;

    return item;
}

/** Collects the languages flagged as newly added, dropping the `false` entries. */
function collectNewLanguages(newLanguages: Record<string, boolean> | undefined): Record<string, boolean> {
    const result: Record<string, boolean> = {};
    for (const [lang, isNewLang] of Object.entries(newLanguages ?? {})) {
        if (isNewLang) result[lang] = true;
    }
    return result;
}

/** Builds the imported-only row for one pending change, or null if it should be dropped. */
function buildImportedOnlyItem(itemId: string, change: ItemChange, selectedLanguages: string[]): MaintenanceItem | null {
    const changedLanguages = Object.keys(change.texts);
    const texts: Record<string, string> = {};
    const changedTexts: Record<string, boolean> = {};
    for (const lang of changedLanguages) {
        texts[lang] = change.texts[lang];
        changedTexts[lang] = true;
    }

    const isDeleted = !!change.isDeleted;
    if (!shouldKeepImportedItem(selectedLanguages, texts, changedLanguages, isDeleted)) return null;

    const importedItem: MaintenanceItem = {
        id: itemId,
        identifier: change.identifier,
        texts,
        originalTexts: { ...texts },
        changedTexts,
    };

    const newTexts = collectNewLanguages(change.newLanguages);
    if (Object.keys(newTexts).length > 0) {
        importedItem.newTexts = newTexts;
        importedItem.isNew = true;
    }
    if (isDeleted) importedItem.isDeleted = true;

    return importedItem;
}

/** Items that exist only as pending changes (imported/added identifiers not in raw). */
function buildImportedOnlyItems(
    changes: Record<string, ItemChange>,
    selectedLanguages: string[],
    existingIdentifiers: Set<string>
): MaintenanceItem[] {
    const importedItems: MaintenanceItem[] = [];

    for (const [itemId, change] of Object.entries(changes)) {
        const identifierName = change.identifier;
        if (!identifierName || existingIdentifiers.has(identifierName)) continue;

        const importedItem = buildImportedOnlyItem(itemId, change, selectedLanguages);
        if (importedItem) importedItems.push(importedItem);
    }

    return importedItems;
}

/**
 * Projects the texts for one rendered row: raw values, then the row's own change,
 * then the identifier-level merged change — each filtered to the included languages.
 */
function collectItemTexts(
    identifierTexts: Array<{ language: string; text: string }>,
    change: ItemChange | undefined,
    merged: MergedChange,
    languagesToInclude: Set<string>
): Record<string, string> {
    const texts: Record<string, string> = {};
    for (const text of identifierTexts) {
        if (languagesToInclude.has(text.language)) texts[text.language] = text.text;
    }
    if (change) {
        for (const [lang, t] of Object.entries(change.texts)) {
            if (languagesToInclude.has(lang)) texts[lang] = t;
        }
    }
    for (const [lang, t] of Object.entries(merged.texts)) {
        if (languagesToInclude.has(lang)) texts[lang] = t;
    }
    return texts;
}

/** Builds the rendered row for one raw identifier, or null if it should not be shown. */
function buildDisplayedItem(
    identifier: Identifier,
    index: number,
    changes: Record<string, ItemChange>,
    changesByIdentifier: Map<string, MergedChange>,
    selectedLanguages: string[]
): MaintenanceItem | null {
    const itemId = `${identifier.id}_${index}`;

    const change = changes[itemId];
    const changedLanguages = change ? Object.keys(change.texts) : [];
    const merged = changesByIdentifier.get(identifier.identifier) ?? EMPTY_MERGED;
    const changedLanguagesByIdentifier = Object.keys(merged.texts);
    const allChangedLanguages = [...new Set([...changedLanguages, ...changedLanguagesByIdentifier])];

    const languagesToInclude = buildLanguagesToInclude(allChangedLanguages, identifier.texts, selectedLanguages);
    const texts = collectItemTexts(identifier.texts, change, merged, languagesToInclude);

    const hasTextInSelected = selectedLanguages.some((lang) => texts[lang] && texts[lang].trim() !== "");
    const hasTrackedChanges = changedLanguages.length > 0 || changedLanguagesByIdentifier.length > 0;
    const isDeletedItem = !!change?.isDeleted || merged.isDeleted;

    if (!hasTextInSelected && !hasTrackedChanges && !isDeletedItem) return null;
    return assembleMaintenanceItem(itemId, identifier.identifier, texts, change, merged);
}

/** Rebuilds the rendered items for one domain from its raw data + pending changes. */
function transformToMaintenanceItems(
    identifiers: Identifier[],
    selectedLanguages: string[],
    changes: Record<string, ItemChange>,
    changesByIdentifier: Map<string, MergedChange>
): MaintenanceItem[] {
    const result: MaintenanceItem[] = [];
    const existingIdentifiers = new Set(identifiers.map((i) => i.identifier));

    for (let i = 0; i < identifiers.length; i++) {
        const item = buildDisplayedItem(identifiers[i], i, changes, changesByIdentifier, selectedLanguages);
        if (item) result.push(item);
    }

    result.push(...buildImportedOnlyItems(changes, selectedLanguages, existingIdentifiers));
    return result;
}

// Operates on freshly-built (plain, non-reactive) items BEFORE they are spliced
// into the reactive store array — mutating plain objects is far cheaper than
// mutating reactive proxies.
function applyPreservedChanges(
    items: MaintenanceItem[],
    changes: Record<string, ItemChange>,
    originals: Record<string, Record<string, string>>
) {
    for (const item of items) {
        if (!originals[item.id] && item.originalTexts) {
            originals[item.id] = { ...item.originalTexts };
        }
        if (originals[item.id]) {
            item.originalTexts = { ...item.originalTexts, ...originals[item.id] };
        }
        const change = changes[item.id];
        if (change) {
            item.changedTexts ??= {};
            item.texts ??= {};
            for (const [lang, t] of Object.entries(change.texts)) {
                item.texts[lang] = t;
                item.changedTexts[lang] = true;
            }
        }
    }
}

/** Finds the raw identifier row, creating an empty one and appending it if absent. */
function findOrCreateRawItem(domain: DomainState, identifier: string): Identifier {
    let rawItem = domain.raw.find((r) => r.identifier === identifier);
    if (!rawItem) {
        rawItem = { id: Date.now(), identifier, texts: [] };
        domain.raw.push(rawItem);
    }
    return rawItem;
}

/** Writes each of the item's per-language texts into the raw row (update or insert). */
function upsertRawTexts(rawItem: Identifier, item: MaintenanceItem) {
    for (const langCode of Object.keys(item.texts)) {
        const entry = rawItem.texts.find((t) => t.language === langCode);
        if (entry) {
            entry.text = item.texts[langCode];
        } else {
            rawItem.texts.push({ id: Date.now(), language: langCode, text: item.texts[langCode] });
        }
    }
}

/**
 * Removes languages that were changed to "no text" (a saved deletion) from the raw
 * row — the same condition buildDomainTextsFromRaw uses to drop them from the save
 * payload — so a later save in this session cannot rebuild and resurrect them.
 */
function purgeSavedDeletions(rawItem: Identifier, item: MaintenanceItem) {
    const changedTexts = item.changedTexts ?? {};
    for (const langCode of Object.keys(changedTexts)) {
        if (changedTexts[langCode] && item.texts[langCode] === undefined) {
            const index = rawItem.texts.findIndex((t) => t.language === langCode);
            if (index !== -1) rawItem.texts.splice(index, 1);
        }
    }
}

/** Patches the raw cache with saved values so future rebuilds reflect what was saved. */
function updateDomainCache(domain: DomainState) {
    for (const item of domain.items) {
        const rawItem = findOrCreateRawItem(domain, item.identifier);
        if (!Array.isArray(rawItem.texts)) continue;
        upsertRawTexts(rawItem, item);
        purgeSavedDeletions(rawItem, item);
    }
    // Identifiers with no texts left in any language no longer exist backend-side.
    domain.raw = domain.raw.filter((r) => !Array.isArray(r.texts) || r.texts.length > 0);
}

function applyBaselineAfterSave(domain: DomainState) {
    // Rows whose deletion was just saved would not exist after a reload — drop them.
    const remaining = domain.items.filter((item) => !item.isDeleted);
    if (remaining.length !== domain.items.length) {
        domain.items.splice(0, domain.items.length, ...remaining);
    }
    for (const item of domain.items) {
        item.originalTexts = { ...item.texts };
        item.changedTexts = {};
        if (item.newTexts) item.newTexts = {};
        if (item.isNew) item.isNew = false;
    }
}

/** Drops a single language's tracked change when its value was reverted to the original. */
function clearRevertedLanguage(domain: DomainState, itemId: string, langCode: string) {
    const change = domain.changes[itemId];
    if (!change) return;
    delete change.texts[langCode];
    delete change.newLanguages?.[langCode];
    if (Object.keys(change.texts).length === 0 && !change.isNew && !change.isDeleted) {
        delete domain.changes[itemId];
    }
}

/**
 * Reconciles an edited item's texts against its originals: a value equal to the
 * original clears that language's tracked change, any other value is applied via
 * `applyEdit` (which records the change on the live item).
 */
function reconcileEditedTexts(
    domain: DomainState,
    updatedItem: MaintenanceItem,
    currentItem: MaintenanceItem,
    applyEdit: (langCode: string, newText: string) => void
) {
    for (const langCode of Object.keys(updatedItem.texts)) {
        const newText = updatedItem.texts[langCode];
        const originalText = currentItem.originalTexts?.[langCode];
        if (originalText === undefined) continue;

        if (newText === originalText) {
            clearRevertedLanguage(domain, updatedItem.id, langCode);
        } else {
            applyEdit(langCode, newText);
        }
    }
}

/** True when a table payload represents an in-place reset (its changed flags are all cleared). */
function isResetPayload(item: MaintenanceItem): boolean {
    return !!item.changedTexts && Object.values(item.changedTexts).every((changed) => changed === false);
}

/** Clears every per-item change flag on a saved/cleared item (in place). */
function resetItemFlags(item: MaintenanceItem) {
    if (item.changedTexts) {
        for (const lang of Object.keys(item.changedTexts)) item.changedTexts[lang] = false;
    }
    if (item.isNew) item.isNew = false;
    if (item.newTexts) item.newTexts = {};
    if (item.isDeleted) item.isDeleted = false;
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useTextMaintenanceStore = defineStore("textMaintenance", {
    state: () => ({
        availableLanguages: [] as MaintenanceLanguage[],
        availableProducts: [] as ProductActivation[],
        productOptions: [] as string[],
        selectedProduct: "",
        activeTabIndex: 0,
        loading: false,
        loadingProducts: false,
        // Set false after the bulk endpoint 404/405s once, so we stop probing a
        // backend that doesn't have it yet and go straight to per-language fetches.
        bulkFetchSupported: true,
        domains: {
            profile: emptyDomain(),
            questionnaire: emptyDomain(),
            default: emptyDomain(),
        } as Record<DomainKey, DomainState>,
    }),

    getters: {
        selectedLanguages(state): string[] {
            return state.availableLanguages.filter((l) => l.selected).map((l) => l.code);
        },
        currentDomainKey(state): DomainKey {
            return domainKeyForTab(state.activeTabIndex);
        },
        currentMaintenanceData(): MaintenanceData {
            return { items: this.domains[this.currentDomainKey].items, selectedLanguages: this.selectedLanguages };
        },
        profileMaintenanceData(): MaintenanceData {
            return { items: this.domains.profile.items, selectedLanguages: this.selectedLanguages };
        },
        questionnaireMaintenanceData(): MaintenanceData {
            return { items: this.domains.questionnaire.items, selectedLanguages: this.selectedLanguages };
        },
        defaultMaintenanceData(): MaintenanceData {
            return { items: this.domains.default.items, selectedLanguages: this.selectedLanguages };
        },
        hasDefaultTexts(state): boolean {
            return state.domains.default.raw.length > 0;
        },
        hasUnsavedChanges(state): boolean {
            const hasItemChanges = DOMAIN_KEYS.some((k) =>
                state.domains[k].items.some(
                    (item) => (item.changedTexts && Object.values(item.changedTexts).includes(true)) || item.isNew
                )
            );
            const hasTracked = DOMAIN_KEYS.some((k) => Object.keys(state.domains[k].changes).length > 0);
            return hasItemChanges || hasTracked;
        },
        hasLanguageChanges(state) {
            return (languageCode: string): boolean =>
                DOMAIN_KEYS.some((k) =>
                    Object.values(state.domains[k].changes).some((ch) => ch.texts[languageCode] !== undefined)
                );
        },
    },

    actions: {
        async loadProducts() {
            try {
                this.loadingProducts = true;
                const productActivations = await LanguageManagementAPI.getAllNamespaces("STAGING");
                this.availableProducts = [...productActivations].sort((a, b) => compareIdentifiers(a.name, b.name));
                const baseProductIds = new Set(
                    productActivations.map((p) => extractBaseProductId(p.name.toLowerCase()))
                );
                this.productOptions = [...baseProductIds].sort(compareIdentifiers);
            } catch (error) {
                console.error("Failed to load products:", error);
            } finally {
                this.loadingProducts = false;
            }
        },

        async fetchTextsForProduct(productId: string) {
            this.loading = true;
            try {
                const availableLanguageCodes = getAvailableLanguageCodes(productId, this.availableProducts);

                const profileTextsMap = new Map<string, any[]>();
                const questionnaireTextsMap = new Map<string, any[]>();
                const defaultTextsMap = new Map<string, any[]>();

                // One request for the whole product. If the bulk endpoint isn't available
                // (older backend), fall back to fetching each language in parallel — and
                // remember it's missing so we don't 404 on every subsequent product load.
                let populated = false;
                if (this.bulkFetchSupported) {
                    const bulk = await populateMapsViaBulk(
                        productId,
                        profileTextsMap,
                        questionnaireTextsMap,
                        defaultTextsMap
                    );
                    populated = bulk.populated;
                    if (bulk.endpointMissing) this.bulkFetchSupported = false;
                }
                if (!populated) {
                    await Promise.all(
                        [...availableLanguageCodes].map((language) =>
                            fetchLanguageData(productId, language, profileTextsMap, questionnaireTextsMap, defaultTextsMap)
                        )
                    );
                }

                this.domains.profile.raw = convertMapToIdentifiers(profileTextsMap);
                this.domains.questionnaire.raw = convertMapToIdentifiers(questionnaireTextsMap);
                this.domains.default.raw = convertMapToIdentifiers(defaultTextsMap);

                // Active tab: default-text products open on the texts tab, others on profile.
                this.activeTabIndex = this.domains.default.raw.length > 0 ? 2 : 0;

                this.availableLanguages.length = 0;
                for (const code of [...availableLanguageCodes].sort((a, b) => a.localeCompare(b))) {
                    this.availableLanguages.push({ code, name: code.toUpperCase(), selected: false, disabled: false });
                }

                if (this.selectedLanguages.length > 0) this.filterMaintenanceData();
            } catch (error) {
                console.error(`Failed to fetch texts for ${productId}:`, error);
                for (const key of DOMAIN_KEYS) {
                    this.domains[key].raw = [];
                    this.domains[key].items = [];
                }
                this.availableLanguages.length = 0;
            } finally {
                this.loading = false;
            }
        },

        filterMaintenanceData() {
            const selectedLangs = this.selectedLanguages;
            const hasAnyChanges = DOMAIN_KEYS.some((k) => Object.keys(this.domains[k].changes).length > 0);

            if (selectedLangs.length === 0 && !hasAnyChanges) {
                for (const key of DOMAIN_KEYS) this.domains[key].items.length = 0;
                return;
            }

            for (const key of DOMAIN_KEYS) {
                const domain = this.domains[key];
                // Read raw/changes without reactive tracking; building the rendered rows
                // is a pure computation and proxy access would dominate for big datasets.
                const rawList = toRaw(domain.raw);
                const changes = toRaw(domain.changes);
                const changesByIdentifier = groupChangesByIdentifier(changes);

                const items = transformToMaintenanceItems(rawList, selectedLangs, changes, changesByIdentifier);
                // Apply preserved changes + sort on the plain objects, THEN publish once.
                applyPreservedChanges(items, changes, domain.originals);
                items.sort((a, b) => compareIdentifiers(a.identifier, b.identifier));
                domain.items.splice(0, domain.items.length, ...items);
            }
        },

        async processLanguageSelection(languages: string[]) {
            for (const lang of this.availableLanguages) {
                lang.selected = languages.includes(lang.code);
                lang.disabled = !lang.selected && languages.length >= 2;
            }
            const hasRaw = DOMAIN_KEYS.some((k) => this.domains[k].raw.length > 0);
            if (this.selectedProduct && hasRaw) this.filterMaintenanceData();
        },

        updateItemText(itemId: string, languageCode: string, newText: string, tabIndex = 0) {
            const key = domainKeyForTab(tabIndex);
            const item = this.domains[key].items.find((i) => i.id === itemId);
            if (item) this.applyChangeToItem(key, item, languageCode, newText);
        },

        /**
         * Records a text change directly on a known item (no lookup). Used by the CSV
         * import, where the item is already in hand — calling updateItemText per cell
         * would re-scan the item list and make a large import O(rows × langs × items).
         */
        applyChangeToItem(key: DomainKey, item: MaintenanceItem, languageCode: string, newText: string) {
            const domain = this.domains[key];
            const change = (domain.changes[item.id] ??= { identifier: item.identifier, texts: {} });
            domain.originals[item.id] ??= {};
            if (domain.originals[item.id][languageCode] === undefined && item.originalTexts?.[languageCode] !== undefined) {
                domain.originals[item.id][languageCode] = item.originalTexts[languageCode];
            }

            change.identifier = item.identifier;
            change.texts[languageCode] = newText;
            if (item.isNew) {
                change.isNew = true;
                change.newLanguages ??= {};
                change.newLanguages[languageCode] = true;
            }

            item.texts[languageCode] = newText;
            item.changedTexts ??= {};
            item.changedTexts[languageCode] = true;
        },

        removeItemChanges(itemId: string, tabIndex = 0) {
            const domain = this.domains[domainKeyForTab(tabIndex)];
            delete domain.changes[itemId];
            delete domain.originals[itemId];
        },

        /** Handles an item update emitted from the table (edit or in-place reset). */
        handleUpdateItem(updatedItem: MaintenanceItem) {
            const domain = this.domains[this.currentDomainKey];
            const index = domain.items.findIndex((i) => i.id === updatedItem.id);
            if (index === -1) return;

            const currentItem = domain.items[index];
            if (isResetPayload(updatedItem)) {
                this.removeItemChanges(updatedItem.id, this.activeTabIndex);
            } else if (updatedItem.texts && currentItem.texts) {
                reconcileEditedTexts(domain, updatedItem, currentItem, (langCode, newText) =>
                    this.updateItemText(updatedItem.id, langCode, newText, this.activeTabIndex)
                );
            }

            domain.items[index] = { ...domain.items[index], ...updatedItem };
        },

        /** Deletes texts for the given languages across all domains. */
        deleteItem(id: string, selectedLanguages: string[]) {
            for (const key of DOMAIN_KEYS) {
                const domain = this.domains[key];
                const item = domain.items.find((i) => i.id === id);
                if (!item) continue;

                const change = (domain.changes[id] ??= { identifier: item.identifier, texts: {} });
                for (const lang of selectedLanguages) {
                    if (item.texts[lang]) {
                        delete item.texts[lang];
                        change.texts[lang] = ""; // empty string marks deletion
                        item.changedTexts ??= {};
                        item.changedTexts[lang] = true;
                    }
                }

                if (Object.keys(item.texts).length === 0) {
                    item.isDeleted = true;
                    change.isDeleted = true;
                }
            }
        },

        /** Adds a new identifier with per-language texts to the current domain. */
        addIdentifier(identifier: string, texts: Record<string, string>): boolean {
            if (!identifier || Object.keys(texts).length === 0) return false;

            const key = this.currentDomainKey;
            const domain = this.domains[key];
            const id = `${key}_${identifier}_${Date.now()}`;

            const itemTexts: Record<string, string> = {};
            const originalTexts: Record<string, string> = {};
            for (const lang of this.availableLanguages) {
                const langText = texts[lang.code] || "";
                itemTexts[lang.code] = langText;
                originalTexts[lang.code] = langText;
            }

            domain.items.push({ id, identifier, texts: itemTexts, originalTexts, changedTexts: {}, isNew: true });
            domain.items.sort((a, b) => compareIdentifiers(a.identifier, b.identifier));

            const change: ItemChange = { identifier, texts: {}, isNew: true, newLanguages: {} };
            for (const lang of this.availableLanguages) {
                const langText = texts[lang.code] || "";
                if (langText.trim()) {
                    change.texts[lang.code] = langText;
                    change.newLanguages![lang.code] = true;
                }
            }
            if (Object.keys(change.texts).length > 0) domain.changes[id] = change;

            return true;
        },

        validateNewIdentifier(identifier: string): { valid: boolean; message?: string } {
            if (!identifier.trim()) return { valid: false, message: "Identifier cannot be empty" };

            const exists = this.domains[this.currentDomainKey].items.some(
                (item) => item.identifier.toLowerCase() === identifier.toLowerCase()
            );
            if (exists) return { valid: false, message: "Identifier already exists" };

            if (!/^[a-zA-Z0-9._-]+$/.test(identifier)) {
                return {
                    valid: false,
                    message: "Identifier can only contain letters, numbers, dots, underscores, and hyphens",
                };
            }
            return { valid: true };
        },

        addLanguage(code: string, name: string): boolean {
            if (!code) return false;
            const lower = code.toLowerCase();
            if (this.availableLanguages.some((l) => l.code.toLowerCase() === lower)) return false;
            this.availableLanguages.push({ code: lower, name: name || code.toUpperCase(), selected: false, disabled: false });
            this.availableLanguages.sort((a, b) => a.code.localeCompare(b.code));
            return true;
        },

        /** Imports a CSV across ALL matched languages (not just the displayed ones). */
        async importCSV(fileContent: string, importType: ImportType): Promise<ImportResult> {
            const key = domainKeyForImportType(importType);
            this.activeTabIndex = tabIndexForImportType(importType);

            const applyChange = (item: MaintenanceItem, langCode: string, newText: string, it: string) => {
                this.applyChangeToItem(domainKeyForImportType(it), item, langCode, newText);
            };

            return importCSVDataImpl(
                fileContent,
                this.availableLanguages,
                this.domains[key],
                applyChange,
                importType,
                {
                    profile: this.domains.profile.raw,
                    questionnaire: this.domains.questionnaire.raw,
                    default: this.domains.default.raw,
                },
                (languages: string[]) => this.processLanguageSelection(languages)
            );
        },

        /** Builds the CSV export content for the given languages and mode. */
        buildExportContent(languages: string[], mode: ImportType): string {
            // For default-text products that were only materialised as items, derive raw on demand.
            const d = this.domains.default;
            if (mode === "texts" && d.raw.length === 0 && d.items.length > 0) {
                d.raw = d.items.map((item, index) => ({
                    id: index + 1,
                    identifier: item.identifier,
                    texts: Object.entries(item.texts).map(([language, text]) => ({
                        id: index + 1,
                        language,
                        text: text as string,
                    })),
                }));
            }
            return generateCSVContentFromRawData(
                this.domains.profile.raw,
                this.domains.questionnaire.raw,
                this.domains.default.raw,
                languages,
                mode
            );
        },

        async saveChanges(): Promise<
            { status: "no-selection" } | { status: "done"; success: boolean; savedCount: number; errors: string[] }
        > {
            if (!this.selectedProduct || this.selectedLanguages.length === 0) {
                return { status: "no-selection" };
            }

            this.loading = true;
            try {
                // Save the displayed languages plus any language with tracked changes
                // (e.g. languages touched by a multi-language CSV import but not shown).
                const languagesToSave = [
                    ...new Set([
                        ...this.selectedLanguages,
                        ...extractLanguagesWithChanges(this.domains.profile.items),
                        ...extractLanguagesWithChanges(this.domains.questionnaire.items),
                        ...extractLanguagesWithChanges(this.domains.default.items),
                    ]),
                ];

                const result = await saveAllChangesToBackend(
                    this.selectedProduct,
                    languagesToSave,
                    this.domains.profile.items,
                    this.domains.questionnaire.items,
                    this.domains.default.items,
                    {
                        profile: this.domains.profile.raw,
                        questionnaire: this.domains.questionnaire.raw,
                        default: this.domains.default.raw,
                    }
                );

                if (result.success) {
                    for (const key of DOMAIN_KEYS) {
                        updateDomainCache(this.domains[key]);
                        applyBaselineAfterSave(this.domains[key]);
                    }
                    this.clearChangedFlags();
                }

                return { status: "done", ...result };
            } catch (error) {
                console.error("Failed to save changes:", error);
                return { status: "done", success: false, savedCount: 0, errors: [String(error)] };
            } finally {
                this.loading = false;
            }
        },

        async resetAllChanges() {
            this.loading = true;
            try {
                for (const key of DOMAIN_KEYS) {
                    const domain = this.domains[key];
                    for (const item of domain.items) {
                        if (!item.originalTexts) continue;
                        item.texts = { ...item.texts };
                        item.changedTexts = { ...item.changedTexts };
                        for (const language of Object.keys(item.originalTexts)) {
                            if (item.originalTexts[language] !== undefined) {
                                item.texts[language] = item.originalTexts[language];
                                item.changedTexts[language] = false;
                            }
                        }
                    }
                    domain.changes = {};
                    domain.originals = {};
                }
            } finally {
                this.loading = false;
            }
        },

        clearChangedFlags() {
            for (const key of DOMAIN_KEYS) {
                const domain = this.domains[key];
                for (const item of domain.items) resetItemFlags(item);
                domain.changes = {};
                domain.originals = {};
            }
        },
    },
});
